import { useEffect, useState, useRef, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Settings2, Trash2, CalendarRange } from "lucide-react";
import { VaultSectionList } from "@/pages/dashboard/VaultSettings";

// Common childcare-program hour windows for the DCY 01229 "during these times"
// blank. Picked from a curated list to avoid format inconsistency across
// centers. "Custom…" reveals a free-text input for unusual cadences.
const HOUR_PRESETS = [
  "6:00 AM - 6:00 PM",
  "6:30 AM - 6:30 PM",
  "7:00 AM - 6:00 PM",
  "7:30 AM - 5:30 PM",
  "8:00 AM - 5:00 PM",
  "8:00 AM - 3:00 PM",
  "8 AM - 3 PM",
  "9:00 AM - 3:00 PM",
  "9:00 AM - 12:00 PM",
  "12:00 PM - 3:00 PM",
  "1:00 PM - 4:00 PM",
];

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function Settings() {
  return (
    <div className="space-y-10 max-w-4xl">
      <header className="space-y-2">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">
          <Settings2 className="w-3.5 h-3.5" /> Center Settings
        </div>
        <h1 className="font-display text-4xl font-medium text-stone-900">Your center, everywhere it matters</h1>
        <p className="text-stone-500 text-base max-w-2xl">
          What you set here auto-fills your DCY transition letters, emails, and printable forms. Each center using ChildCheckOh has their own settings — never shared across accounts.
        </p>
      </header>

      <DCY01229DefaultsCard />
      <ContactInfoCard />
      <OperatingDaysCard />
      <AutoReviewCard />
      <SUTQParticipationCard />
      <ObservedHolidaysCard />
      <EmailNotificationsCard />
      <TuitionRatesCard />
      <CenterLogoCard />
      <VaultPasscodesCard />
    </div>
  );
}


// ============ Section 5: Vault passcodes (per sensitive area) ============
function VaultPasscodesCard() {
  return (
    <SectionCard
      testId="settings-vault-card"
      eyebrow="Security"
      title="Vault passcodes"
      desc="Each sensitive area (Records, Staff Docs & Training, Licensing File, Compliance, Playground, Billing) has its own passcode. Change, reset, or lock any of them independently — owner-only."
    >
      <VaultSectionList />
    </SectionCard>
  );
}


// ============ Section 1: DCY 01229 defaults (director's name + hours) ============
function DCY01229DefaultsCard() {
  const [name, setName] = useState("");
  const [savedName, setSavedName] = useState("");
  const [hours, setHours] = useState("");
  const [savedHours, setSavedHours] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingName, setSavingName] = useState(false);
  const [savingHours, setSavingHours] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/center/me")
      .then((r) => {
        if (cancelled) return;
        const d = r.data?.director_name || "";
        const h = r.data?.transition_visit_times || "";
        setName(d); setSavedName(d);
        setHours(h); setSavedHours(h);
        if (h && !HOUR_PRESETS.includes(h)) {
          setCustomMode(true);
          setCustomText(h);
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const onSelectHours = (val) => {
    if (val === "__custom__") {
      setCustomMode(true);
      setCustomText(savedHours && !HOUR_PRESETS.includes(savedHours) ? savedHours : "");
    } else {
      setCustomMode(false);
      setCustomText("");
      setHours(val);
    }
  };

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Director's name cannot be empty"); return; }
    setSavingName(true);
    try {
      await api.put("/center/director-name", { director_name: trimmed });
      setSavedName(trimmed);
      toast.success("Director's name saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingName(false); }
  };

  const saveHours = async () => {
    const trimmed = (customMode ? customText : hours).trim();
    if (!trimmed) { toast.error("Pick or type the hours."); return; }
    setSavingHours(true);
    try {
      await api.put("/center/transition-defaults", { transition_visit_times: trimmed });
      setHours(trimmed);
      setSavedHours(trimmed);
      toast.success("Standard hours saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingHours(false); }
  };

  if (loading) return <SectionSkeleton />;
  const nameDirty = name.trim() !== savedName.trim();
  const pending = customMode ? customText.trim() : hours.trim();
  const hoursDirty = pending && pending !== savedHours.trim();
  const selectValue = customMode ? "__custom__" : (HOUR_PRESETS.includes(hours) ? hours : "");

  return (
    <SectionCard
      testId="settings-dcy01229-card"
      eyebrow="DCY 01229 transition letters"
      title="Director's name & standard hours"
      desc="Stamped on every classroom transition letter. Days are always Monday – Friday."
    >
      <FieldRow
        label="Director's name (Administrator field)"
        hint="Use the licensed director's personal name — not the daycare name."
        input={
          <Input
            data-testid="settings-director-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. AbdulQadir Moalim"
            maxLength={120}
            className="h-10"
          />
        }
        saveBtn={
          <SaveBtn dirty={nameDirty} saving={savingName} onClick={saveName} testId="settings-director-name-save" />
        }
      />

      <FieldRow
        label='Standard hours ("during these times")'
        hint="Pick the window your families typically visit during."
        input={
          <>
            <select
              data-testid="settings-hours-select"
              value={selectValue}
              onChange={(e) => onSelectHours(e.target.value)}
              className="w-full h-10 rounded-md border border-stone-200 px-2 text-sm bg-white"
            >
              <option value="" disabled>Choose your center&apos;s hours…</option>
              {HOUR_PRESETS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
              <option value="__custom__">Custom…</option>
            </select>
            {customMode && (
              <Input
                data-testid="settings-hours-custom"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="e.g. 9:00 AM – 12:30 PM"
                maxLength={60}
                className="mt-2 h-10"
              />
            )}
          </>
        }
        saveBtn={
          <SaveBtn dirty={!!hoursDirty} saving={savingHours} onClick={saveHours} testId="settings-hours-save" />
        }
      />
    </SectionCard>
  );
}


// ============ Section 1b: Operating days (iter-81) ============
// A tiny card that lets the owner pick which days of the week their center
// operates. The choice powers (1) the Attendance page's day toggle default,
// (2) the DCY 01208 weekly report's `days` param, and (3) a visual "cross
// line" on non-operating days so a Sat-Sun daycare never sees Mon-Fri
// columns without the strike-through hint.
const OPERATING_DAY_OPTIONS = [
  { value: "mon-fri", label: "Monday – Friday",  hint: "Weekday-only care (most Ohio centers)", days: [1,2,3,4,5] },
  { value: "sun-sat", label: "Sunday – Saturday", hint: "Open all 7 days (24/7 or weekend-inclusive care)", days: [0,1,2,3,4,5,6] },
  { value: "sat-sun", label: "Saturday – Sunday", hint: "Weekend-only care (church nurseries, respite programs)", days: [0,6] },
];
const DAY_INITIALS = ["S","M","T","W","T","F","S"];

export function operatingDaysToArray(op) {
  return OPERATING_DAY_OPTIONS.find((o) => o.value === op)?.days || [1,2,3,4,5];
}

function OperatingDaysCard() {
  const [op, setOp] = useState("mon-fri");
  const [savedOp, setSavedOp] = useState("mon-fri");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const r = await api.get("/center/me");
        if (!ignore) {
          const v = r.data?.operating_days || "mon-fri";
          setOp(v); setSavedOp(v);
        }
      } catch { /* first-time centers may not have this yet */ }
      finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, []);
  const dirty = op !== savedOp;
  const days = operatingDaysToArray(op);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/center/profile", { operating_days: op });
      setSavedOp(op);
      toast.success("Operating days saved. Attendance page + reports updated.");
      // Broadcast so the Attendance page reloads on next visit without a hard refresh.
      window.dispatchEvent(new CustomEvent("cc:operating-days-changed", { detail: { operating_days: op } }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  if (loading) return <SectionSkeleton />;
  return (
    <SectionCard
      title="Operating days"
      subtitle="Which days of the week is your center open? Non-operating days get a strike-through across the app so nobody accidentally checks kids in on a Saturday."
      icon={<CalendarRange className="w-4 h-4" />}
      data-testid="operating-days-card"
    >
      <div className="space-y-3">
        {OPERATING_DAY_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            data-testid={`op-days-radio-${opt.value}`}
            className={`flex items-start gap-3 rounded-2xl border p-4 cursor-pointer transition ${op === opt.value ? "border-emerald-600 bg-emerald-50/60" : "border-stone-200 hover:border-stone-400 bg-white"}`}
          >
            <input
              type="radio"
              name="operating_days"
              value={opt.value}
              checked={op === opt.value}
              onChange={() => setOp(opt.value)}
              className="mt-1 accent-emerald-600"
            />
            <div className="flex-1">
              <div className="font-semibold text-stone-900">{opt.label}</div>
              <div className="text-xs text-stone-500 mt-0.5">{opt.hint}</div>
              <div className="mt-2 inline-flex items-center gap-1" data-testid={`op-days-preview-${opt.value}`}>
                {DAY_INITIALS.map((letter, i) => {
                  const on = opt.days.includes(i);
                  return (
                    <span
                      key={i}
                      className={`inline-grid place-items-center w-7 h-7 rounded-lg text-xs font-bold border ${on
                        ? "bg-emerald-700 text-white border-emerald-700"
                        : "bg-stone-100 text-stone-400 border-stone-200 line-through"}`}
                      title={on ? "Open" : "Closed"}
                    >
                      {letter}
                    </span>
                  );
                })}
              </div>
            </div>
          </label>
        ))}
        {/* Live preview of the current selection */}
        <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 flex items-center gap-3">
          <span className="text-xs uppercase tracking-widest font-semibold text-stone-500">Live preview</span>
          {DAY_INITIALS.map((letter, i) => {
            const on = days.includes(i);
            return (
              <span
                key={i}
                data-testid={`op-days-live-${i}`}
                className={`inline-grid place-items-center w-8 h-8 rounded-lg text-sm font-bold border ${on
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-stone-400 border-stone-200 line-through opacity-60"}`}
              >
                {letter}
              </span>
            );
          })}
          <div className="ml-auto">
            <SaveBtn dirty={dirty} saving={saving} onClick={save} testId="op-days-save" />
          </div>
        </div>
      </div>
    </SectionCard>
  );
}


// ============ Section 2: Contact info ============
function ContactInfoCard() {
  const [data, setData] = useState({ phone: "", contact_email: "", address_line1: "", address_line2: "", city: "", state: "", zip_code: "", county: "", license_number: "" });
  const [saved, setSaved] = useState(data);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/center/me")
      .then((r) => {
        if (cancelled) return;
        const next = {
          phone: r.data?.phone || "",
          contact_email: r.data?.contact_email || "",
          address_line1: r.data?.address_line1 || "",
          address_line2: r.data?.address_line2 || "",
          city: r.data?.city || "",
          state: r.data?.state || "",
          zip_code: r.data?.zip_code || "",
          county: r.data?.county || "",
          license_number: r.data?.license_number || "",
        };
        setData(next);
        setSaved(next);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/center/profile", data);
      setSaved(data);
      toast.success("Contact info saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <SectionSkeleton />;
  const dirty = JSON.stringify(data) !== JSON.stringify(saved);

  const setF = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <SectionCard
      testId="settings-contact-card"
      eyebrow="Contact"
      title="Center contact info"
      desc="Shown in emails, family communications, and on printable forms."
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Phone" testId="settings-phone">
          <Input value={data.phone} onChange={(e) => setF("phone", e.target.value)} placeholder="(614) 555-0100" maxLength={30} className="h-10" data-testid="settings-phone-input" />
        </Field>
        <Field label="Contact email" testId="settings-contact-email">
          <Input type="email" value={data.contact_email} onChange={(e) => setF("contact_email", e.target.value)} placeholder="info@yourcenter.com" maxLength={120} className="h-10" data-testid="settings-contact-email-input" />
        </Field>
        <Field label="Ohio License #" testId="settings-license-number">
          <Input value={data.license_number} onChange={(e) => setF("license_number", e.target.value)} placeholder="e.g. 000123456" maxLength={60} className="h-10" data-testid="settings-license-number-input" />
        </Field>
        <Field label="Street address" testId="settings-address-line1">
          <Input value={data.address_line1} onChange={(e) => setF("address_line1", e.target.value)} placeholder="123 Main St" maxLength={120} className="h-10" data-testid="settings-address-line1-input" />
        </Field>
        <Field label="Address line 2 (optional)" testId="settings-address-line2">
          <Input value={data.address_line2} onChange={(e) => setF("address_line2", e.target.value)} placeholder="Suite 100" maxLength={120} className="h-10" data-testid="settings-address-line2-input" />
        </Field>
        <Field label="City" testId="settings-city">
          <Input value={data.city} onChange={(e) => setF("city", e.target.value)} placeholder="Columbus" maxLength={60} className="h-10" data-testid="settings-city-input" />
        </Field>
        <div className="grid grid-cols-[1fr_1fr] gap-3">
          <Field label="State" testId="settings-state">
            <select value={data.state} onChange={(e) => setF("state", e.target.value)} className="h-10 w-full rounded-md border border-stone-200 px-2 text-sm bg-white" data-testid="settings-state-select">
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="ZIP" testId="settings-zip">
            <Input value={data.zip_code} onChange={(e) => setF("zip_code", e.target.value)} placeholder="43215" maxLength={20} className="h-10" data-testid="settings-zip-input" />
          </Field>
        </div>
        <Field label="County (Ohio) — prints on DCY 01299 incident reports & other state forms" testId="settings-county">
          <Input
            value={data.county}
            onChange={(e) => setF("county", e.target.value)}
            placeholder="Franklin"
            maxLength={60}
            className="h-10"
            data-testid="settings-county-input"
          />
        </Field>
      </div>
      <div className="flex justify-end">
        <SaveBtn dirty={dirty} saving={saving} onClick={save} testId="settings-contact-save" widthClass="w-32" />
      </div>
    </SectionCard>
  );
}


// ============ Section 3: Tuition rates per classroom ============
function TuitionRatesCard() {
  const [rates, setRates] = useState({ infant: "", toddler: "", preschool: "", school_age: "" });
  const [saved, setSaved] = useState(rates);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/center/me")
      .then((r) => {
        if (cancelled) return;
        const tr = r.data?.tuition_rates || {};
        const next = {
          infant: tr.infant || "",
          toddler: tr.toddler || "",
          preschool: tr.preschool || "",
          school_age: tr.school_age || "",
        };
        setRates(next);
        setSaved(next);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/center/tuition-rates", rates);
      setSaved(rates);
      toast.success("Tuition rates saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <SectionSkeleton />;
  const dirty = JSON.stringify(rates) !== JSON.stringify(saved);
  const setF = (k, v) => setRates((r) => ({ ...r, [k]: v }));

  return (
    <SectionCard
      testId="settings-tuition-card"
      eyebrow="Tuition"
      title="Weekly tuition rates by classroom"
      desc="Auto-fills the “current rate of tuition in this room is $___” blank on every DCY 01229 transition letter, based on the destination classroom."
    >
      <div className="grid sm:grid-cols-2 gap-4">
        {[
          ["infant", "Infants"],
          ["toddler", "Toddlers"],
          ["preschool", "Preschool"],
          ["school_age", "School-age"],
        ].map(([key, label]) => (
          <Field key={key} label={label} testId={`settings-tuition-${key}`}>
            <Input
              value={rates[key]}
              onChange={(e) => setF(key, e.target.value)}
              placeholder="$295/week"
              maxLength={40}
              className="h-10"
              data-testid={`settings-tuition-${key}-input`}
            />
          </Field>
        ))}
      </div>
      <p className="text-[11px] text-stone-500">
        Write the amount however you bill — e.g. <i>$295/week</i>, <i>$1,200/month</i>, <i>Sliding scale — call 614-555-0100</i>. Leave blank to keep the letter&apos;s blank empty.
      </p>
      <div className="flex justify-end">
        <SaveBtn dirty={dirty} saving={saving} onClick={save} testId="settings-tuition-save" widthClass="w-32" />
      </div>
    </SectionCard>
  );
}


// ============ Section 3a: Observed Holidays (iter-81n) ============
function ObservedHolidaysCard() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [catalog, setCatalog] = useState([]);
  const [observed, setObserved] = useState(new Set());
  const [saved, setSaved] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 1, y, y + 1, y + 2];
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/holidays?year=${year}`),
      api.get(`/center/holidays`),
    ]).then(([cat, cur]) => {
      if (cancelled) return;
      setCatalog(cat.data.holidays || []);
      const set = new Set(cur.data.observed || []);
      setObserved(set);
      setSaved(new Set(set));
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [year]);

  const toggle = (key) => {
    setObserved((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const arr = Array.from(observed);
      await api.put("/center/holidays", { observed: arr });
      setSaved(new Set(arr));
      toast.success("Observed holidays saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const printPDF = async () => {
    try {
      const r = await api.get(`/center/holidays.pdf?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const dirty = observed.size !== saved.size || Array.from(observed).some((k) => !saved.has(k));
  const grouped = useMemo(() => {
    const g = { federal: [], christian: [], muslim: [], other: [] };
    for (const r of catalog) (g[r.category] || g.other).push(r);
    return g;
  }, [catalog]);

  if (loading) return <SectionSkeleton />;

  return (
    <SectionCard
      testId="settings-holidays-card"
      eyebrow="Schedule"
      title="Observed holidays"
      desc="Pick the holidays your center closes on. Print a one-page sheet for the front desk anytime — US federal + common Christian + Muslim holidays are included."
    >
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Year</Label>
          <select
            data-testid="holidays-year-select"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="mt-1 h-10 rounded-xl border border-stone-200 px-3 text-sm bg-white"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <Button
          data-testid="holidays-print"
          variant="outline"
          onClick={printPDF}
          className="rounded-xl h-10"
        >
          Print / download PDF
        </Button>
        <div className="ml-auto">
          <SaveBtn dirty={dirty} saving={saving} onClick={save} testId="settings-holidays-save" widthClass="w-40" />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {[
          { key: "federal", title: "US Federal", accent: "bg-emerald-700" },
          { key: "christian", title: "Christian", accent: "bg-amber-600" },
          { key: "muslim", title: "Muslim", accent: "bg-sky-700" },
        ].map((grp) => (
          <div key={grp.key} data-testid={`holidays-group-${grp.key}`} className="rounded-2xl border border-stone-200 overflow-hidden bg-white">
            <div className={`${grp.accent} text-white px-4 py-2 text-xs uppercase tracking-widest font-semibold`}>{grp.title}</div>
            <div className="divide-y divide-stone-100">
              {(grouped[grp.key] || []).map((r) => (
                <label
                  key={r.key}
                  data-testid={`holiday-row-${r.key}`}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-stone-50"
                >
                  <input
                    data-testid={`holiday-check-${r.key}`}
                    type="checkbox"
                    checked={observed.has(r.key)}
                    onChange={() => toggle(r.key)}
                    className="w-4 h-4 accent-emerald-700"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900">{r.label}</div>
                    <div className="text-xs text-stone-500">{r.weekday} · {new Date(r.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                  </div>
                </label>
              ))}
              {(grouped[grp.key] || []).length === 0 && (
                <div className="px-4 py-6 text-center text-xs text-stone-400">None for this year.</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}


// ============ Section 3b: Email notification frequency ============
// iter-81k — per-kind frequency control. Users complained about daily
// emails; each kind can be Off / Daily / Weekly / Monthly independently.
const EMAIL_PREF_ROWS = [
  { key: "nightly_digest",             label: "Compliance digest",         desc: "PDF summary of staff docs, medical exams, playground and licensing gaps." },
  { key: "staff_docs_expiring",        label: "Staff document expiries",   desc: "Reminder listing every staff credential expired or expiring within 30 days." },
  { key: "playground_overdue",         label: "Playground inspection",     desc: "Reminder when your DCY 01281 monthly inspection is overdue." },
  { key: "med_exam_reminder",          label: "Child medical exam",        desc: "Parent-facing reminder when a child's medical exam is expired/expiring (routed via the center)." },
  { key: "weekly_attendance_archive",  label: "Weekly attendance archive", desc: "Friday-afternoon email with every classroom's DCY 01208 attached." },
  { key: "monthly_staff_digest",       label: "Monthly staff digest",      desc: "First-of-month PDF showing every staff member's training + document compliance." },
  { key: "auto_classroom_transition",  label: "Auto classroom transition", desc: "Email when children auto-graduate to a new classroom." },
];
const FREQ_CHOICES = [
  { v: "off",     label: "Off",     hint: "Never send" },
  { v: "daily",   label: "Daily",   hint: "Every day" },
  { v: "weekly",  label: "Weekly",  hint: "At most once every 7 days" },
  { v: "monthly", label: "Monthly", hint: "At most once every 28 days" },
];


// ============ iter-81s — DCY 01234 auto 12-month review ============
function AutoReviewCard() {
  const [enabled, setEnabled] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const r = await api.get("/center/me");
        if (!ignore) {
          const v = !!r.data?.auto_review_12mo;
          setEnabled(v); setSaved(v);
        }
      } catch { /* new center */ }
      finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/center/profile", { auto_review_12mo: enabled });
      setSaved(enabled);
      toast.success(enabled ? "Auto 12-month review turned ON." : "Auto 12-month review turned OFF.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/enrollment/auto-review-run");
      const n = r.data?.reviews_added || 0;
      toast.success(n > 0 ? `Added ${n} new 12-month review${n === 1 ? "" : "s"}.` : "No reviews were due today.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  if (loading) return <SectionSkeleton />;
  const dirty = enabled !== saved;
  return (
    <SectionCard
      testId="auto-review-card"
      eyebrow="Enrollment"
      title="DCY 01234 · Auto 12-month review"
      desc="When ON, every child's enrollment form auto-stamps a fresh review row 12 months after their last sign-off (parent + director initials pulled from the record on file). Run it manually anytime with the button below."
    >
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-review-toggle-label">
          <input
            data-testid="auto-review-toggle"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 accent-emerald-700"
          />
          <span className="text-sm font-medium text-stone-900">Auto-stamp every 12 months</span>
        </label>
        <div className="ml-auto flex gap-2">
          <Button
            data-testid="auto-review-run-now"
            variant="outline"
            onClick={runNow}
            disabled={running}
            className="rounded-xl h-10"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            Run now
          </Button>
          <SaveBtn dirty={dirty} saving={saving} onClick={save} testId="auto-review-save" widthClass="w-32" />
        </div>
      </div>
      <p className="text-xs text-stone-500 mt-3">
        The scheduler runs this pass daily at 6&nbsp;AM Eastern for centers that have the toggle ON. Any DCY 01234 whose latest signature or review is 365+ days old gets a fresh row appended with the stored initials.
      </p>
    </SectionCard>
  );
}


/**
 * iter-81c4 — SUTQ participation switch.
 * When ON, the SUTQ / Quality Requirements section on every staff
 * compliance checklist is enforced (rows print CURRENT/MISSING/EXPIRING).
 * When OFF, those rows auto-print "N/A — SUTQ only" so directors of
 * non-SUTQ centers aren't chased about quality-rating requirements they
 * don't have to meet.
 */
function SUTQParticipationCard() {
  const [enabled, setEnabled] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const r = await api.get("/center/me");
        if (!ignore) {
          const v = !!r.data?.participates_in_sutq;
          setEnabled(v); setSaved(v);
        }
      } catch { /* new center */ }
      finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/center/profile", { participates_in_sutq: enabled });
      setSaved(enabled);
      toast.success(enabled ? "SUTQ participation turned ON." : "SUTQ participation turned OFF — quality rows now print N/A.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <SectionSkeleton />;
  const dirty = enabled !== saved;
  return (
    <SectionCard
      testId="sutq-participation-card"
      eyebrow="Compliance"
      title="Step Up To Quality (SUTQ) participation"
      desc="Turn this ON only if your center participates in Ohio's Step Up To Quality rating system. When OFF, the SUTQ / Quality Requirements section on every staff compliance checklist automatically prints N/A — so you're never chased about quality-rating rules you don't have to meet."
    >
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="sutq-toggle-label">
          <input
            data-testid="sutq-toggle"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 accent-emerald-700"
          />
          <span className="text-sm font-medium text-stone-900">Our center participates in SUTQ</span>
        </label>
        <div className="ml-auto">
          <SaveBtn dirty={dirty} saving={saving} onClick={save} testId="sutq-save" widthClass="w-32" />
        </div>
      </div>
      <p className="text-xs text-stone-500 mt-3">
        Applies to every staff compliance PDF and dashboard. SUTQ rows include the two SUTQ PD Topic Areas (min 1 hour each in different topics, annually).
      </p>
    </SectionCard>
  );
}


function EmailNotificationsCard() {
  const [prefs, setPrefs] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/center/email-prefs")
      .then((r) => {
        if (cancelled) return;
        setPrefs(r.data || {});
        setSaved(r.data || {});
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/center/email-prefs", prefs);
      setSaved(prefs);
      toast.success("Notification preferences saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const setAll = (v) => {
    const next = {};
    for (const row of EMAIL_PREF_ROWS) next[row.key] = v;
    setPrefs(next);
  };

  if (loading) return <SectionSkeleton />;
  const dirty = JSON.stringify(prefs) !== JSON.stringify(saved);

  return (
    <SectionCard
      testId="settings-email-prefs-card"
      eyebrow="Notifications"
      title="Email frequency — you decide"
      desc="Pick how often you want each ChildCheck reminder to land in your inbox. Off means we'll never send that kind again."
    >
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          data-testid="email-prefs-all-off"
          type="button"
          onClick={() => setAll("off")}
          className="px-3 h-8 rounded-full text-xs font-semibold border border-stone-300 text-stone-700 hover:bg-stone-50"
        >Turn everything off</button>
        <button
          data-testid="email-prefs-all-weekly"
          type="button"
          onClick={() => setAll("weekly")}
          className="px-3 h-8 rounded-full text-xs font-semibold border border-emerald-300 text-emerald-800 hover:bg-emerald-50"
        >Set all to Weekly</button>
        <button
          data-testid="email-prefs-all-monthly"
          type="button"
          onClick={() => setAll("monthly")}
          className="px-3 h-8 rounded-full text-xs font-semibold border border-emerald-300 text-emerald-800 hover:bg-emerald-50"
        >Set all to Monthly</button>
      </div>
      <div className="rounded-2xl border border-stone-200 divide-y divide-stone-100 bg-white">
        {EMAIL_PREF_ROWS.map((row) => {
          const val = prefs[row.key] || "weekly";
          return (
            <div key={row.key} data-testid={`email-pref-row-${row.key}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-stone-900 text-sm">{row.label}</div>
                <div className="text-xs text-stone-500 mt-0.5">{row.desc}</div>
              </div>
              <div data-testid={`email-pref-picker-${row.key}`} className="inline-flex rounded-xl border border-stone-200 bg-stone-50 overflow-hidden self-start sm:self-auto">
                {FREQ_CHOICES.map((c) => (
                  <button
                    key={c.v}
                    type="button"
                    data-testid={`email-pref-${row.key}-${c.v}`}
                    onClick={() => setPrefs((p) => ({ ...p, [row.key]: c.v }))}
                    title={c.hint}
                    className={`px-3 h-9 text-xs font-semibold transition-colors ${val === c.v ? "bg-emerald-700 text-white" : "text-stone-700 hover:bg-white"}`}
                  >{c.label}</button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-end mt-4">
        <SaveBtn dirty={dirty} saving={saving} onClick={save} testId="settings-email-prefs-save" widthClass="w-40" />
      </div>
    </SectionCard>
  );
}



// ============ Section 4: Logo upload ============
function CenterLogoCard() {
  const fileRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(null);   // object URL of the current logo
  const [hasLogo, setHasLogo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await api.get("/center/logo", { responseType: "blob", validateStatus: () => true });
      if (r.status === 200) {
        const blob = new Blob([r.data], { type: r.data.type || "image/png" });
        setLogoUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
        setHasLogo(true);
      } else {
        setHasLogo(false);
      }
    } catch { setHasLogo(false); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    return () => { if (logoUrl) URL.revokeObjectURL(logoUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (e) => {
    e?.preventDefault?.();
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Pick a PNG or JPG first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/center/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated.");
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (err) {
      const msg = err?.response?.status === 413 ? "File too large (max 2 MB)"
        : err?.response?.status === 415 ? "Only PNG or JPG accepted"
        : formatApiError(err);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the current logo?")) return;
    setBusy(true);
    try {
      await api.delete("/center/logo");
      toast.success("Logo removed.");
      setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      setHasLogo(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (loading) return <SectionSkeleton />;

  return (
    <SectionCard
      testId="settings-logo-card"
      eyebrow="Brand"
      title="Center logo"
      desc="PNG or JPG, up to 2 MB. Square images render best. Shown in the future on PDFs and parent emails."
    >
      <div className="flex flex-col sm:flex-row items-start gap-6">
        <div data-testid="settings-logo-preview" className="w-32 h-32 rounded-2xl border border-stone-200 bg-stone-50 flex items-center justify-center overflow-hidden">
          {hasLogo && logoUrl ? (
            <img src={logoUrl} alt="Center logo" className="w-full h-full object-contain" />
          ) : (
            <span className="text-xs text-stone-400 text-center px-3">No logo yet</span>
          )}
        </div>
        <form onSubmit={upload} className="flex-1 space-y-3">
          <input
            ref={fileRef}
            data-testid="settings-logo-file"
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            className="block w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-emerald-700 file:text-white file:text-xs"
          />
          <div className="flex gap-2">
            <Button type="submit" data-testid="settings-logo-upload" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (hasLogo ? "Replace logo" : "Upload logo")}
            </Button>
            {hasLogo && (
              <Button type="button" data-testid="settings-logo-delete" onClick={remove} disabled={busy} variant="outline" className="rounded-xl h-10 text-rose-700 border-rose-200 hover:bg-rose-50">
                <Trash2 className="w-4 h-4 mr-1.5" /> Remove
              </Button>
            )}
          </div>
        </form>
      </div>
    </SectionCard>
  );
}


// ============ Tiny shared building blocks ============
function SectionCard({ testId, eyebrow, title, desc, children }) {
  return (
    <section data-testid={testId} className="rounded-3xl border border-stone-200 bg-white p-8 space-y-5">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">{eyebrow}</div>
        <h2 className="mt-2 font-display text-2xl text-stone-900">{title}</h2>
        {desc ? <p className="mt-1 text-sm text-stone-500 max-w-2xl">{desc}</p> : null}
      </header>
      {children}
    </section>
  );
}

function Field({ label, hint, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint ? <p className="mt-1 text-[11px] text-stone-500">{hint}</p> : null}
    </div>
  );
}

function FieldRow({ label, hint, input, saveBtn }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
      <div className="flex-1">
        <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
        <div className="mt-1.5">{input}</div>
        {hint ? <p className="mt-1 text-[11px] text-stone-500">{hint}</p> : null}
      </div>
      <div className="sm:pt-[22px]">{saveBtn}</div>
    </div>
  );
}

function SaveBtn({ dirty, saving, onClick, testId, widthClass = "w-32" }) {
  return (
    <Button
      data-testid={testId}
      onClick={onClick}
      disabled={saving || !dirty}
      className={`bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10 ${widthClass}`}
    >
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (dirty ? "Save" : "Saved")}
    </Button>
  );
}

function SectionSkeleton() {
  return <section className="rounded-3xl border border-stone-200 bg-white p-8 h-48 animate-pulse" />;
}
